import { Button } from '@signate-cloud/ui-lib/src/components/atoms/Button';
import { Checkbox } from '@signate-cloud/ui-lib/src/components/atoms/Checkbox';
import { Input } from '@signate-cloud/ui-lib/src/components/slims/Input';
import type { AxiosError } from 'axios';
import { useResetAtom } from 'jotai/utils';
import { isOnPremises } from 'libs/env';
import { emailValidator } from 'libs/validators';
import { contracts } from 'locales/ja/contracts';
import { organizationUsers } from 'locales/ja/organization_users';
import type { GetServerSidePropsContext, GetServerSidePropsResult } from 'next';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import type { SubmitHandler } from 'react-hook-form';
import { toast } from 'react-toastify';
import type { FormValidationError } from 'types/cloud_api_error_type';
import { isValidFormValidationError } from 'types/cloud_api_error_type';
import { HtmlHead } from '@/components/atoms/HtmlHead';
import { IncidentsArea } from '@/components/slims/IncidentsArea';
import type { IncidentsResponse } from 'apis/admins/incidents/incidents_response_type';
import { getIncidents } from 'apis/admins/incidents/search';
import type { SignInRequest } from 'apis/sign_in';
import { postSignIn } from 'apis/sign_in';
import type { SignateNotificationsUnsignedInResponse } from 'apis/signate_notifications/un_signed_in';
import { getSignateNotificationsUnSignedIn } from 'apis/signate_notifications/un_signed_in';
import { passwordResetPage } from 'pages/password/reset/[token]/pages';
import {
  approvedContractsAtom,
  authoritiesAtom,
  currentAdminAtom,
  currentOrganizationAdminAtom,
  finishedAllLicensesAtom,
  organizationBillAddressesAtom,
  organizationsAtom,
} from 'utils/atoms';
import {
  fieldState,
  getFirstErrorField,
  getFieldValidationError,
  hasHttpErrorMessage,
  httpErrorHandler,
  removeFieldValidationError,
} from 'utils/httpError';
import { copyright, signInAzureSamlPage, signInPage } from '../pages';
import { passwordResetRequestPage } from '../password/reset_request/pages';
import { OutOfServiceAnnouncement } from './OutOfServiceAnnouncement';
import { SignateNotificationCarousel } from './SignateNotificationCarousel';
import { checkAdminsMe, checkOrganizationAdminsMe } from './signIn';

export const getServerSideProps = (
  context: GetServerSidePropsContext
): GetServerSidePropsResult<{ redirectUrl: string }> => {
  return {
    props: {
      redirectUrl: String(context.query.redirect_url ?? ''),
    },
  };
};

const SignInPage = ({ redirectUrl }: { redirectUrl: string }) => {
  const router = useRouter();
  const [incidents, setIncidents] = useState<IncidentsResponse[]>([]);
  const [signateNotifications, setSignateNotifications] =
    useState<SignateNotificationsUnsignedInResponse>();
  const resetCurrentAdmin = useResetAtom(currentAdminAtom);
  const resetCurrentOrganizationAdmin = useResetAtom(
    currentOrganizationAdminAtom
  );
  const resetAuthorities = useResetAtom(authoritiesAtom);
  const resetApprovedContracts = useResetAtom(approvedContractsAtom);
  const resetFinishedAllLicenses = useResetAtom(finishedAllLicensesAtom);
  const resetOrganizations = useResetAtom(organizationsAtom);
  const resetOrganizationBillAddresses = useResetAtom(
    organizationBillAddressesAtom
  );
  const apiCalledRef = useRef(false);
  const {
    register,
    getValues,
    trigger,
    setFocus,
    clearErrors,
    handleSubmit,
    formState: { errors },
  } = useForm<SignInRequest>({
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    shouldUnregister: false,
    criteriaMode: 'all',
    shouldFocusError: true,
  });
  const [formValidationError, setFormValidationError] =
    useState<FormValidationError>({
      errorType: 'error',
      message: '',
      details: [],
    });
  const [isFormErrorOccurred, setIsFormErrorOccurred] =
    useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  // eslint-disable-next-line sonarjs/cognitive-complexity
  const onSubmit: SubmitHandler<SignInRequest> = async (request) => {
    trigger().then((isValid) => {
      if (!isValid) return;
    });

    setIsSubmitting(true);
    postSignIn(request)
      .then((response) => {
        if (response.status === 200) {
          if (!response.data.success && response.data.resetPasswordToken) {
            router?.push({
              pathname: `${passwordResetPage.path}/${response.data.resetPasswordToken}`,
              query: { forceChange: true },
            });
          } else {
            checkOrganizationAdminsMe(router, redirectUrl);
          }
        }
      })
      .catch((error: AxiosError) => {
        setIsSubmitting(false);
        setIsFormErrorOccurred(hasHttpErrorMessage(error));
        if (error.response && isValidFormValidationError(error.response.data)) {
          setFormValidationError(error.response.data as FormValidationError);
          const errorField: keyof SignInRequest | undefined =
            getFirstErrorField(error.response.data);
          if (errorField) setFocus(errorField);
        }
        httpErrorHandler(router, error, {
          noRedirection: error.response?.status === 401,
        });
      });
  };

  const resetSessionAtoms = useCallback(() => {
    resetCurrentAdmin();
    resetCurrentOrganizationAdmin();
    resetAuthorities();
    resetApprovedContracts();
    resetFinishedAllLicenses();
    resetOrganizations();
    resetOrganizationBillAddresses();
  }, [
    resetCurrentAdmin,
    resetCurrentOrganizationAdmin,
    resetAuthorities,
    resetApprovedContracts,
    resetFinishedAllLicenses,
    resetOrganizations,
    resetOrganizationBillAddresses,
  ]);

  useEffect(() => {
    if (!router?.isReady) return;

    setFocus('email');
    // See: https://github.com/reactwg/react-18/discussions/18#discussion-3385714
    if (apiCalledRef.current === false) {
      apiCalledRef.current = true;
      if (!checkAdminsMe(router, redirectUrl)) {
        // NOTE :セッション切れ／なしのため、 atom クリア
        resetSessionAtoms();
      }
      getIncidents().then((response) => {
        if (response.status === 200) setIncidents(response.data.incidents);
      });
      getSignateNotificationsUnSignedIn({ targetPage: 'sign_in' })
        .then((response) => {
          setSignateNotifications(response.data);
        })
        .catch((error: AxiosError) => {
          httpErrorHandler(router, error, { noRedirection: true });
        });
    }
  }, [setFocus, router, redirectUrl, resetSessionAtoms]);

  // バックエンドからリダイレクト時に一度だけエラー表示
  useEffect(() => {
    if (!router?.isReady) return;

    const error = router.query.error as string;
    if (error && error != '') {
      toast.error(error);
      const { pathname, query } = router;
      delete query.error;
      router?.replace({ pathname, query: query }, undefined, { shallow: true });
    }
  }, [router]);

  const clearFormAndFieldError = (field: keyof SignInRequest) => {
    setIsFormErrorOccurred(false);
    clearErrors(field);
    removeFieldValidationError(field, formValidationError);
  };

  return (
    <>
      <HtmlHead title={signInPage.title} description={signInPage.description} />
      <IncidentsArea incidents={incidents} />
      <div className="d-md-flex flex-row-reverse">
        <div className="signin-right">
          <div className="signin-box signup">
            <h3 className="signin-title-secondary mg-b-30">
              {signInPage.title}
            </h3>
            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="form-group">
                <Input
                  id="sign-in-email"
                  type="email"
                  autoComplete="email"
                  placeholder="メールアドレスをご入力ください"
                  label="メールアドレス"
                  state={fieldState(
                    isFormErrorOccurred,
                    'email',
                    formValidationError,
                    errors.email
                  )}
                  fieldError={errors.email}
                  validationError={getFieldValidationError(
                    'email',
                    formValidationError
                  )}
                  required
                  {...register('email', {
                    ...emailValidator,
                    onBlur: () => {
                      if (getValues('email') !== null)
                        clearFormAndFieldError('email');
                    },
                  })}
                />
              </div>
              <div className="form-group">
                <Input
                  id="sign-in-password"
                  type="password"
                  autoComplete="new-password"
                  placeholder="パスワードをご入力ください"
                  label="パスワード"
                  state={fieldState(
                    isFormErrorOccurred,
                    'password',
                    formValidationError,
                    errors.password
                  )}
                  fieldError={errors.password}
                  validationError={getFieldValidationError(
                    'password',
                    formValidationError
                  )}
                  required
                  {...register('password', {
                    // 文字数の最低が6=>8に変更になったのでフロントで認証用のパスワードに
                    // バリデーションかけるとそもそも次に進めなくなるケースがあるのでpasswordValidatorは使用しない
                    required: '入力してください',
                    onBlur: () => {
                      if (getValues('password') !== null)
                        clearFormAndFieldError('password');
                    },
                  })}
                />
              </div>
              <div className="form-group mg-t-30 mg-b-50">
                <Checkbox
                  label="サインイン状態を保持する"
                  classNames={{ root: 'mg-b-40' }}
                  {...register('rememberMe')}
                />
              </div>
              <Button
                className="btn-block btn-signin"
                type="submit"
                disabled={isSubmitting}
              >
                <i className="fa fa-sign-in mg-r-5"></i>
                サインイン
              </Button>
            </form>
            <p className="mg-b-5">
              パスワードを忘れた方は
              <Link href={passwordResetRequestPage.path} rel="noreferrer">
                こちら
              </Link>
            </p>
            {!isOnPremises() && (
              <p className="mg-b-20">
                その他でお困りの方は
                <Link
                  href={`${process.env.NEXT_PUBLIC_SIGNATE_CLOUD_SUPPORT_INQUIRIES_URL}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  こちら
                </Link>
              </p>
            )}
            <div>
              <Button
                type="button"
                onClick={() =>
                  router?.push({
                    pathname: signInAzureSamlPage.path,
                    query: redirectUrl ? { redirect_url: redirectUrl } : {},
                  })
                }
                className="btn-block"
                style={{ textTransform: 'none' }}
                data-testid="azure-sign-in-button"
              >
                Azure AD
                <span className="mg-l-5 mg-r-5">で</span>
                サインイン
              </Button>
            </div>
            <div>
              <Button
                type="button"
                onClick={() =>
                  router?.push({
                    pathname: 'sign_in/okta_saml',
                    query: redirectUrl ? { redirect_url: redirectUrl } : {},
                  })
                }
                className="btn-block mg-t-20"
                style={{ textTransform: 'none' }}
                data-testid="okta-sign-in-button"
              >
                Okta
                <span className="mg-l-5 mg-r-5">で</span>
                サインイン
              </Button>
            </div>
          </div>
        </div>
        <div className="signin-left">
          <div className="signin-box">
            <h2 className="slim-logo">
              <Image
                src="/shared-assets/images/cloud.png"
                alt="SIGNATE Cloud"
                width={360}
                height={87}
              />
            </h2>
            <OutOfServiceAnnouncement />
            <SignateNotificationCarousel
              signateNotificationsResource={signateNotifications}
            />
            <p>
              SIGNATE Cloud 組織管理画面のサインインページです。
              <br />
              組織管理画面では、{organizationUsers.resourceName}や
              {contracts.resourceName}
              の管理や、アセスメントテストの管理、講座の進捗状況などをモニタリングすることができます。
            </p>
            {!isOnPremises() && (
              <div>
                <p>
                  {contracts.resourceName}
                  に関するご相談は、お問い合わせボタンをクリックして、お問い合わせフォームよりお問い合わせください。
                  <br />
                </p>
                <div className="d-flex justify-content-center mg-b-15">
                  <Link
                    className="btn btn-outline-secondary pd-x-25"
                    target="_blank"
                    href={'https://signate.co.jp/contact'}
                    rel="noreferrer"
                  >
                    お問い合わせ
                  </Link>
                </div>
              </div>
            )}
            <p className="tx-12 mg-t-30">{copyright}</p>
          </div>
        </div>
      </div>
    </>
  );
};

export default SignInPage;
